import { isEqual } from 'lodash';
import { createSelector } from 'reselect';

import { splitFullName } from 'shared/lib/formatters/splitFullName';

import { selectContactsInfoData, selectPassportInfoData } from 'entities/creditForm';
import type { TScoringRequest } from 'entities/scoring';
import { getUserScoreSelector } from 'entities/scoring';

type TUpdateScoreValueState = {
  queryData: TScoringRequest;
  isNeedUpdateScore: boolean;
};

const isDataReadyToSend = (queryData: TScoringRequest) => {
  return Object.keys(queryData).every((key: string) => {
    const qyeryKey = key as keyof typeof queryData;
    if (['middleName', 'isResidenceAddressMatch'].includes(qyeryKey)) {
      return true;
    }

    return !!queryData[qyeryKey];
  });
};

const sameUserData = (queryData: TScoringRequest, scoredUserData: string) => {
  try {
    const restoredScoredData = JSON.parse(scoredUserData);

    return isEqual(queryData, restoredScoredData);
  } catch (error) {
    return true;
  }
};

export const updateScoreValueSelector = createSelector(
  getUserScoreSelector,
  selectPassportInfoData,
  selectContactsInfoData,
  (scoreData, passport, contacts): TUpdateScoreValueState => {
    const {
      birthDate,
      passportNumber,
      passportIssuedBy,
      registrationAddress,
      unitCode,
      residenceAddress,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      residenceAddressIsRegistration,
      passportIssueDate,
      birthPlace,
      fullName,
    } = passport;
    const { email, mobilePhone } = contacts;
    const { firstName, lastName, middleName } = splitFullName(fullName?.value || '');
    const { scoredUserData, score } = scoreData;

    const queryData = {
      birthDate: birthDate || '',
      birthPlace,
      email,
      firstName,
      lastName,
      middleName: middleName || undefined,
      isResidenceAddressMatch: residenceAddressIsRegistration,
      passportIssueDate: passportIssueDate || '',
      passportIssuedBy,
      passportNumber: (passportNumber || '').substring(4),
      passportSerial: (passportNumber || '').substring(0, 4),
      phone: mobilePhone,
      registrationAddress: registrationAddress?.value || '',
      residenceAddress: residenceAddress?.value || '',
      unitCode,
    };

    const isNeedUpdateScore = isDataReadyToSend(queryData) && (!score || !sameUserData(queryData, scoredUserData));

    return {
      isNeedUpdateScore,
      queryData,
    };
  },
);
